import { useEffect } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

/**
 * Invite-email section for the Engagement Wizard, driven by the DB
 * `email_template` collection instead of hardcoded frontend bodies.
 *
 * Default behavior sends NO override, so the backend resolves a template per
 * invite from the recipient's audience (`template_for_invite`). That is what
 * makes an edit in /admin-console/email-templates actually reach the wizard's
 * sends — previously this step always posted `email_html` built from constants
 * in inviteEmailTemplates.js, which bypassed those templates entirely.
 *
 * "Customize" is therefore an explicit opt-in override: one body applied to
 * every recipient in the batch, replacing per-audience selection.
 *
 * NOTE: the older InviteEmailSection.jsx is still used by ActionModePanel
 * (the AI copilot send path) and is intentionally left alone — converting that
 * path is a separate piece of work.
 */

const quillModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

function EyeIcon({ className = "w-4 h-4" }) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" className={className} aria-hidden>
      <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
      <path
        fillRule="evenodd"
        d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
        clipRule="evenodd"
      />
    </svg>
  );
}

/** Mirrors v2.engagements.audiences.resolve_audience_key. Advisory only — this
 *  drives the "who gets what" summary; the backend remains authoritative for
 *  the template actually used on each send. */
export function resolveAudienceKey(designation, audiences) {
  const wanted = String(designation || "").trim().toLowerCase();
  if (wanted) {
    for (const a of audiences) {
      if (a.is_default) continue;
      if ((a.match_designations || []).some((d) => String(d).trim().toLowerCase() === wanted)) {
        return a.key;
      }
    }
  }
  return audiences.find((a) => a.is_default)?.key || audiences[0]?.key || "";
}

export default function WizardInviteEmailSection({
  participants = [],
  recipientEmails = [],
  templates = [],
  audiences = [],
  customized = false,
  onCustomizedChange,
  editorHtml = "",
  onEditorChange,
  expanded = false,
  onToggleExpanded,
  onPreviewTemplate,
  disabled = false,
  hideSendButton = false,
  sendDisabled = false,
  onSend,
  sending = false,
  sendLabel = "Send invites",
  compact = false,
  isResend = false,
}) {
  const templateFor = (audienceKey) =>
    templates.find((t) => t.role_group === audienceKey) || null;

  // Only show audiences actually represented in this batch; fall back to all
  // of them before any participant has been entered.
  const presentKeys = [
    ...new Set(
      participants
        .map((p) => p.designation)
        .filter(Boolean)
        .map((d) => resolveAudienceKey(d, audiences))
    ),
  ];
  const shownAudiences = (
    presentKeys.length ? audiences.filter((a) => presentKeys.includes(a.key)) : audiences
  ).filter(Boolean);

  const primaryTemplate = templateFor(shownAudiences[0]?.key) || templates[0] || null;

  // Seed the editor from a real DB template the first time the admin opens
  // customization, so they start from what would actually be sent.
  useEffect(() => {
    if (expanded && !editorHtml && primaryTemplate?.content_html) {
      onEditorChange?.(primaryTemplate.content_html);
    }
  }, [expanded, editorHtml, primaryTemplate, onEditorChange]);

  const emails =
    recipientEmails.length > 0
      ? recipientEmails
      : participants.map((p) => p.email?.trim()).filter(Boolean);

  return (
    <section className={compact ? "space-y-3" : "border-t pt-6 mt-6 space-y-4"}>
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className={`font-semibold text-brand ${compact ? "text-sm" : ""}`}>
            {isResend ? "Reminder email" : "Invitation email"}
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {customized
              ? "Using your customized message for every recipient in this batch."
              : "Each recipient gets the saved template for their audience."}
          </p>
        </div>
        {onToggleExpanded ? (
          <button
            type="button"
            className="text-sm text-[#004f73] font-medium hover:underline shrink-0 text-left"
            onClick={() => onToggleExpanded(!expanded)}
          >
            {expanded ? "Hide email customization" : "Customize this email"}
          </button>
        ) : null}
      </div>

      {!customized ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Emails this send will use
          </p>
          {shownAudiences.length ? (
            shownAudiences.map((a) => {
              const tpl = templateFor(a.key);
              return (
                <div key={a.key} className="flex items-center gap-2 text-xs py-0.5">
                  <span className="text-slate-500 shrink-0 w-32 truncate" title={a.label}>
                    {a.label}
                  </span>
                  <span
                    className={`flex-1 min-w-0 truncate ${
                      tpl ? "text-gray-800 font-medium" : "text-amber-700 italic"
                    }`}
                    title={tpl?.label || "No saved template for this audience"}
                  >
                    {tpl?.label || "No saved template for this audience"}
                  </span>
                  <button
                    type="button"
                    className="shrink-0 p-1 rounded text-[#004f73] hover:bg-[#004f73]/10 disabled:opacity-25 disabled:cursor-not-allowed"
                    onClick={() => onPreviewTemplate?.(tpl.id, a.label)}
                    disabled={!tpl}
                    aria-label={`Preview the ${a.label} email`}
                    title={tpl ? "Preview this email" : "No template to preview"}
                  >
                    <EyeIcon />
                  </button>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-slate-400 py-1">
              Add participants to see which template each will receive.
            </p>
          )}
        </div>
      ) : (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <p className="text-xs text-amber-800 flex-1">
            This customized message replaces the per-audience templates and goes to{" "}
            <strong>every recipient</strong> in this batch.
          </p>
          <button
            type="button"
            className="text-xs font-medium text-amber-900 hover:underline shrink-0"
            onClick={() => {
              onCustomizedChange?.(false);
              onEditorChange?.("");
            }}
          >
            Use saved templates
          </button>
        </div>
      )}

      {expanded ? (
        <>
          <p className="text-xs text-gray-500">
            Editing below overrides the saved templates for this send only. Saved templates are
            managed in Mail Manager.
          </p>
          <div className={disabled ? "opacity-60 pointer-events-none" : ""}>
            <ReactQuill
              theme="snow"
              value={editorHtml}
              onChange={(html) => {
                onEditorChange?.(html);
                if (!customized) onCustomizedChange?.(true);
              }}
              modules={quillModules}
              className="invite-email-quill bg-white"
            />
          </div>
          <p className="text-xs text-gray-500">
            The assessment link is added automatically when the email is sent.
          </p>
        </>
      ) : null}

      {!hideSendButton ? (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 rounded-lg border">
          <span className="text-gray-700 text-sm truncate">
            {emails.length ? emails.join(", ") : "Recipient emails appear here"}
          </span>
          <button
            type="button"
            className="blue_btn shrink-0 px-6 disabled:opacity-50"
            disabled={sendDisabled || sending}
            onClick={onSend}
          >
            {sending ? "Sending…" : sendLabel}
          </button>
        </div>
      ) : null}
    </section>
  );
}
